import React from "react";
import { motion } from "framer-motion";
import { Dialog, DialogPortal, DialogOverlay } from "./ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  X,
  GraduationCap,
  Briefcase,
  Sparkles,
  Award,
  Globe2,
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
} from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

const FacultyDetailModal = ({ profile, open, onOpenChange, accent = "#00BCD4" }) => {
  if (!profile) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/70 backdrop-blur-md" />
        <DialogPrimitive.Content
          data-testid="faculty-detail-modal"
          className="fixed left-1/2 top-1/2 z-[60] w-[95vw] max-w-4xl h-[90vh] -translate-x-1/2 -translate-y-1/2 bg-[#FAF8F5] rounded-2xl shadow-2xl overflow-hidden border border-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300"
        >
          <DialogPrimitive.Close
            data-testid="faculty-modal-close"
            className="absolute top-5 right-5 z-30 w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 backdrop-blur-md flex items-center justify-center text-gray-700 transition-all hover:rotate-90"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </DialogPrimitive.Close>

          {/* Watermark initials */}
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 0.06, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: "easeOut" }}
            className="pointer-events-none absolute -right-12 -top-20 text-[240px] font-extralight leading-none select-none tracking-tighter"
            style={{ color: accent }}
          >
            JHL
          </motion.div>

          <div className="relative flex flex-col h-full overflow-y-auto">
            {/* Top meta */}
            <motion.div
              {...fade(0.08)}
              className="flex items-center gap-3 px-8 md:px-14 pt-7 pb-3"
            >
              <span
                className="px-2.5 py-1 text-[10px] font-mono tracking-[0.3em] uppercase text-white rounded-sm"
                style={{ background: accent }}
              >
                Faculty
              </span>
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-400">
                Keimyung AI Research Center · Principal Investigator
              </span>
            </motion.div>

            {/* Header */}
            <header className="px-8 md:px-14 pt-2 pb-7">
              <div className="flex items-start gap-8">
                <div className="flex-1 min-w-0">
                  <motion.h2
                    {...fade(0.16)}
                    className="text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 tracking-tight leading-[1.02]"
                  >
                    {profile.name}
                  </motion.h2>
                  {profile.nameKr && (
                    <motion.p
                      {...fade(0.2)}
                      className="mt-2 text-base text-gray-500 font-light"
                    >
                      {profile.nameKr}
                    </motion.p>
                  )}
                  <motion.p
                    {...fade(0.24)}
                    className="mt-4 text-sm md:text-base font-medium tracking-[0.05em]"
                    style={{ color: accent }}
                  >
                    {profile.title}
                  </motion.p>
                  <motion.div
                    {...fade(0.32)}
                    className="mt-6 pt-5 border-t border-dashed border-gray-300 text-xs text-gray-600 font-light space-y-1"
                  >
                    <div>{profile.affiliation}</div>
                    {profile.departments && (
                      <div className="text-gray-500">{profile.departments}</div>
                    )}
                  </motion.div>
                </div>

                {/* Photo */}
                {profile.photo && (
                  <motion.div
                    {...fade(0.2)}
                    className="hidden md:block flex-shrink-0"
                  >
                    <div
                      className="relative w-[140px] h-[170px] rounded-xl overflow-hidden shadow-md bg-gray-100"
                      style={{ border: `1px solid ${accent}33` }}
                    >
                      <img
                        src={profile.photo}
                        alt={profile.name}
                        className="absolute inset-0 w-full h-full object-cover object-top"
                      />
                      <div
                        className="absolute top-1.5 left-1.5 w-3 h-3 border-l border-t"
                        style={{ borderColor: "#ffffffaa" }}
                      />
                      <div
                        className="absolute top-1.5 right-1.5 w-3 h-3 border-r border-t"
                        style={{ borderColor: "#ffffffaa" }}
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            </header>

            {/* Body */}
            <div className="px-8 md:px-14 pb-12 space-y-12">
              {/* Bio */}
              <motion.div {...fade(0.4)} className="max-w-3xl relative">
                <span
                  className="absolute -left-7 -top-1 text-5xl font-serif leading-none"
                  style={{ color: accent }}
                >
                  “
                </span>
                <p className="text-base md:text-lg font-light leading-[1.75] text-gray-800">
                  {profile.bio}
                </p>
              </motion.div>

              {/* Education */}
              {profile.education && (
                <motion.section {...fade(0.46)}>
                  <SectionLabel icon={GraduationCap} accent={accent}>
                    Education
                  </SectionLabel>
                  <div className="space-y-1">
                    {profile.education.map((e, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-[5rem_1fr] gap-4 items-start py-3 border-b border-gray-200 last:border-b-0"
                      >
                        <div
                          className="text-xs font-mono pt-0.5"
                          style={{ color: accent }}
                        >
                          {e.years}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {e.degree}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5 font-light">
                            {e.school}
                          </div>
                          {e.note && (
                            <div className="text-xs text-gray-500 italic mt-1 font-light">
                              {e.note}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Career */}
              {profile.career && (
                <motion.section {...fade(0.52)}>
                  <SectionLabel icon={Briefcase} accent={accent}>
                    Career
                  </SectionLabel>
                  <div className="space-y-1">
                    {profile.career.map((c, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-[6rem_1fr] gap-4 items-start py-3 border-b border-gray-200 last:border-b-0"
                      >
                        <div
                          className="text-xs font-mono pt-0.5"
                          style={{ color: accent }}
                        >
                          {c.years}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {c.role}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5 font-light">
                            {c.org}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Research Interests */}
              {profile.interests && (
                <motion.section {...fade(0.58)}>
                  <SectionLabel icon={Sparkles} accent={accent}>
                    Research Interests
                  </SectionLabel>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {profile.interests.map((it, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2.5 text-sm text-gray-700 font-light leading-relaxed"
                      >
                        <span
                          className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                          style={{ background: accent }}
                        />
                        {it}
                      </li>
                    ))}
                  </ul>
                </motion.section>
              )}

              {/* Honors */}
              {profile.honors && (
                <motion.section {...fade(0.64)}>
                  <SectionLabel icon={Award} accent={accent}>
                    Honors & Memberships
                  </SectionLabel>
                  <ul className="space-y-2">
                    {profile.honors.map((h, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2.5 text-sm text-gray-700 font-light"
                      >
                        <span
                          className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                          style={{ background: accent }}
                        />
                        {h}
                      </li>
                    ))}
                  </ul>
                </motion.section>
              )}

              {/* Collaborations */}
              {profile.collaborations && (
                <motion.section {...fade(0.7)}>
                  <SectionLabel icon={Globe2} accent={accent}>
                    Collaborations
                  </SectionLabel>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {profile.collaborations.international && (
                      <div className="p-5 bg-white rounded-xl border border-gray-100">
                        <div
                          className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2"
                          style={{ color: accent }}
                        >
                          International
                        </div>
                        <ul className="space-y-1 text-sm text-gray-700 font-light">
                          {profile.collaborations.international.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {profile.collaborations.domestic && (
                      <div className="p-5 bg-white rounded-xl border border-gray-100">
                        <div
                          className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2"
                          style={{ color: accent }}
                        >
                          Domestic (Korea)
                        </div>
                        <ul className="space-y-1 text-sm text-gray-700 font-light">
                          {profile.collaborations.domestic.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.section>
              )}

              {/* Contact */}
              {profile.contact && (
                <motion.section {...fade(0.76)}>
                  <SectionLabel icon={Mail} accent={accent}>
                    Contact
                  </SectionLabel>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {profile.contact.email && (
                      <a
                        href={`mailto:${profile.contact.email}`}
                        data-testid="faculty-contact-email"
                        className="group flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-300 transition-colors"
                      >
                        <Mail
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: accent }}
                        />
                        <div className="min-w-0">
                          <div
                            className="text-[10px] font-mono uppercase tracking-[0.25em]"
                            style={{ color: accent }}
                          >
                            Email
                          </div>
                          <div className="text-sm text-gray-900 font-medium truncate">
                            {profile.contact.email}
                          </div>
                        </div>
                      </a>
                    )}
                    {profile.contact.phone && (
                      <a
                        href={`tel:${profile.contact.phone.replace(/[^\d+]/g, "")}`}
                        data-testid="faculty-contact-phone"
                        className="group flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-300 transition-colors"
                      >
                        <Phone
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: accent }}
                        />
                        <div className="min-w-0">
                          <div
                            className="text-[10px] font-mono uppercase tracking-[0.25em]"
                            style={{ color: accent }}
                          >
                            Phone
                          </div>
                          <div className="text-sm text-gray-900 font-medium">
                            {profile.contact.phone}
                          </div>
                        </div>
                      </a>
                    )}
                    {profile.contact.address && (
                      <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-100">
                        <MapPin
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: accent }}
                        />
                        <div className="min-w-0">
                          <div
                            className="text-[10px] font-mono uppercase tracking-[0.25em]"
                            style={{ color: accent }}
                          >
                            Address
                          </div>
                          <div className="text-xs text-gray-700 font-light leading-relaxed mt-0.5">
                            {profile.contact.address}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.section>
              )}

              {/* Source */}
              {profile.sourceUrl && (
                <motion.div
                  {...fade(0.82)}
                  className="pt-6 border-t border-gray-200 flex items-center justify-between"
                >
                  <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-400">
                    Profile source · AICD Lab Wiki
                  </span>
                  <a
                    href={profile.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-1.5 text-xs font-light text-gray-700 hover:text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5 transition-colors"
                  >
                    Full faculty page
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </a>
                </motion.div>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

const SectionLabel = ({ icon: Icon, children, accent }) => (
  <div className="flex items-end justify-between mb-5 pb-3 border-b border-dashed border-gray-300">
    <div className="flex items-center gap-2.5">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      <h3 className="text-sm font-medium tracking-[0.1em] uppercase text-gray-700">
        {children}
      </h3>
    </div>
  </div>
);

export default FacultyDetailModal;
